import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CompanyDeregistrationReason } from '../../../models/companyDeregistrationReason';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-company-deregistration-reason-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './company-deregistration-reason-list.component.html',
  styleUrl: './company-deregistration-reason-list.component.scss'
})
export class CompanyDeregistrationReasonListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  companyDeregistrationReasons = signal<CompanyDeregistrationReason[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCompanyDeregistrationReasons();
  }

  loadCompanyDeregistrationReasons(): void {
    this.spinnerService.show();
    this.lutService.getCompanyDeregistrationReasons().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortCompanyDeregistrationReasons(data);
        this.companyDeregistrationReasons.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading company deregistration reasons:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortCompanyDeregistrationReasons(reasons: CompanyDeregistrationReason[]): CompanyDeregistrationReason[] {
    return [...reasons].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateCompanyDeregistrationReason(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(companyDeregistrationReason: CompanyDeregistrationReason): void {
  }

  onEditCompanyDeregistrationReason(companyDeregistrationReason: CompanyDeregistrationReason): void {
  }

  onDeleteCompanyDeregistrationReason(companyDeregistrationReason: CompanyDeregistrationReason): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(companyDeregistrationReason.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalReasons = this.companyDeregistrationReasons();

          // 1. Optimistically remove from local state immediately
          this.companyDeregistrationReasons.update(reasons => reasons.filter(r => r.id !== companyDeregistrationReason.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCompanyDeregistrationReason(companyDeregistrationReason.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Company deregistration reason deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting company deregistration reason:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.companyDeregistrationReasons.set(originalReasons);
              this.toast.error(`<strong>Failed to delete Company Deregistration Reason:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: CompanyDeregistrationReason, newName: string} | {item: CompanyDeregistrationReason, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: CompanyDeregistrationReason;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.companyDeregistrationReasons.update(reasons => {
      const updated = reasons.map(r => r.id === event.item.id ? updatedItem : r);
      return this.sortCompanyDeregistrationReasons(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateCompanyDeregistrationReason(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Company deregistration reason updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.companyDeregistrationReasons.update(reasons => {
          const updated = reasons.map(r => r.id === event.item.id ? response : r);
          return this.sortCompanyDeregistrationReasons(updated);
        });
      },
      error: (error) => {
        console.error('Error updating company deregistration reason:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.companyDeregistrationReasons.update(reasons =>
          reasons.map(r => r.id === event.item.id ? originalItem : r)
        );
        this.toast.error(`<strong>Failed to update Company Deregistration Reason:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newReason: CompanyDeregistrationReason = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.companyDeregistrationReasons.update(reasons => this.sortCompanyDeregistrationReasons([...reasons, newReason]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createCompanyDeregistrationReason({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Company deregistration reason created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.companyDeregistrationReasons.update(reasons => {
          const updated = reasons.map(r => r.id === tempId ? response : r);
          return this.sortCompanyDeregistrationReasons(updated);
        });
      },
      error: (error) => {
        console.error('Error creating company deregistration reason:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.companyDeregistrationReasons.update(reasons =>
          reasons.filter(r => r.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Company Deregistration Reason:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
